import {
    addDays,
    addMonths,
    addWeeks,
    addYears,
    differenceInCalendarDays,
    format,
    parseISO,
    startOfDay,
} from 'date-fns';
import type { DashboardFilter } from './dashboardConfig';
import type { DashboardRollingPeriod } from './dashboardRollingDates';
import { isRollingPeriod, rollingDateRange } from './dashboardRollingDates';

// The maths behind a single-value widget's comparison line. Pure, so it is tested without
// rendering anything.

export const DASHBOARD_COMPARISON_UNITS = ['days', 'weeks', 'months', 'years'] as const;
export type DashboardComparisonUnit = (typeof DASHBOARD_COMPARISON_UNITS)[number];

// What a single value is compared with.
//   previousPeriod – the stretch just before, as long as the widget's own: last 7 days against
//                    the 7 before, this month so far against last month as far, 1–14 March
//                    against 15–28 February
//   shift          – the same period a set time earlier (one year earlier is "last year")
//   dates          – a fixed range, from and to inclusive, on `field` or every dated filter
export type DashboardStatComparison =
    | { kind: 'previousPeriod' }
    | { kind: 'shift'; amount: number; unit: DashboardComparisonUnit }
    | { kind: 'dates'; from: string; to: string; field?: string };

export const LAST_YEAR: DashboardStatComparison = { kind: 'shift', amount: 1, unit: 'years' };

type QueryFilter = { field: string; operator: string; value: string | number | string[] };
// A date window: from `start` up to, not including, `end`. Either end can be open.
type Window = { start?: Date; end?: Date };

const DATE_PREFIX = /^\d{4}-\d{2}-\d{2}/;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const BOUND_OPERATORS = ['gte', 'gt', 'lte', 'lt'];

const asDate = (value: unknown): Date | null => {
    if (typeof value !== 'string' || !DATE_PREFIX.test(value)) return null;
    const date = parseISO(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const day = (date: Date) => format(date, 'yyyy-MM-dd');

const shiftBack = (date: Date, amount: number, unit: DashboardComparisonUnit): Date => {
    if (unit === 'days') return addDays(date, -amount);
    if (unit === 'weeks') return addWeeks(date, -amount);
    if (unit === 'months') return addMonths(date, -amount);
    return addYears(date, -amount);
};

// The previous period of a calendar window is the one before it on the calendar — this week
// against last week, this quarter so far against last quarter as far — rather than however
// many days it has run so far.
const ROLLING_STEP: Record<DashboardRollingPeriod, [number, DashboardComparisonUnit]> = {
    today: [1, 'days'],
    yesterday: [1, 'days'],
    last_7_days: [7, 'days'],
    last_14_days: [14, 'days'],
    last_30_days: [30, 'days'],
    last_90_days: [90, 'days'],
    this_week: [1, 'weeks'],
    last_week: [1, 'weeks'],
    this_month: [1, 'months'],
    last_month: [1, 'months'],
    this_quarter: [3, 'months'],
    last_quarter: [3, 'months'],
    year_to_date: [1, 'years'],
    last_12_months: [12, 'months'],
    last_year: [1, 'years'],
};

// The filters a comparison query runs with: every date window moved to the period being
// compared with, everything else as it was. Null when there is nothing to compare with — no
// date filter to move, or set dates not filled in.
export const comparisonQueryFilters = (
    filters: DashboardFilter[],
    comparison: DashboardStatComparison | undefined,
    now: Date = new Date(),
): QueryFilter[] | null => {
    if (!comparison) return null;
    const tomorrow = addDays(startOfDay(now), 1);
    const windows = new Map<string, Window>();
    // The rolling period a field's window came from, when that is all it came from.
    const rollingOf = new Map<string, DashboardRollingPeriod | null>();
    const others: QueryFilter[] = [];
    const narrow = (field: string, next: Window) => {
        const current = windows.get(field) ?? {};
        windows.set(field, {
            start:
                current.start && next.start
                    ? new Date(Math.max(+current.start, +next.start))
                    : current.start ?? next.start,
            end:
                current.end && next.end
                    ? new Date(Math.min(+current.end, +next.end))
                    : current.end ?? next.end,
        });
    };

    filters.forEach(({ field, operator, value, rolling }) => {
        if (isRollingPeriod(rolling)) {
            const { start, endExclusive } = rollingDateRange(rolling, now);
            rollingOf.set(field, windows.has(field) ? null : rolling);
            narrow(field, { start, end: endExclusive });
            return;
        }
        const date = BOUND_OPERATORS.includes(operator) ? asDate(value) : null;
        if (!date) {
            others.push({ field, operator, value });
            return;
        }
        rollingOf.set(field, null);
        // A bare date on an upper bound means the whole of that day.
        const endOfDay = DATE_ONLY.test(String(value)) ? addDays(date, 1) : date;
        if (operator === 'gte') narrow(field, { start: date });
        else if (operator === 'gt')
            narrow(field, { start: addDays(date, DATE_ONLY.test(String(value)) ? 1 : 0) });
        else if (operator === 'lte') narrow(field, { end: endOfDay });
        else narrow(field, { end: date });
    });

    const compared = new Map<string, Window>();
    if (comparison.kind === 'dates') {
        const from = asDate(comparison.from);
        const to = asDate(comparison.to);
        if (!from || !to) return null;
        const fields = comparison.field ? [comparison.field] : [...windows.keys()];
        fields.forEach(field => compared.set(field, { start: from, end: addDays(to, 1) }));
    } else {
        windows.forEach((window, field) => {
            // A window still open at the end runs to today; one open at the start has no
            // length, so only a set shift can move it.
            const end = window.end ?? (window.start ? tomorrow : undefined);
            if (comparison.kind === 'shift') {
                const amount = Math.max(1, Math.floor(comparison.amount) || 1);
                compared.set(field, {
                    start: window.start && shiftBack(window.start, amount, comparison.unit),
                    end: end && shiftBack(end, amount, comparison.unit),
                });
                return;
            }
            const rolling = rollingOf.get(field);
            if (rolling) {
                const [amount, unit] = ROLLING_STEP[rolling];
                compared.set(field, {
                    start: window.start && shiftBack(window.start, amount, unit),
                    end: end && shiftBack(end, amount, unit),
                });
            } else if (window.start && end) {
                const length = differenceInCalendarDays(end, window.start);
                compared.set(field, { start: addDays(window.start, -length), end: window.start });
            }
        });
    }
    if (compared.size === 0) return null;

    const moved = [...compared.entries()].flatMap(([field, { start, end }]) => [
        ...(start ? [{ field, operator: 'gte', value: day(start) }] : []),
        ...(end ? [{ field, operator: 'lt', value: day(end) }] : []),
    ]);
    // Any other window, on a field the comparison doesn't move, stays as it was.
    const kept = [...windows.entries()]
        .filter(([field]) => !compared.has(field))
        .flatMap(([field, { start, end }]) => [
            ...(start ? [{ field, operator: 'gte', value: day(start) }] : []),
            ...(end ? [{ field, operator: 'lt', value: day(end) }] : []),
        ]);
    return [...others, ...kept, ...moved];
};

// The change from the earlier figure to this one, as a whole percentage; null when there is no
// earlier figure to compare with. Measured against the size of the earlier figure, so a negative
// figure improving towards zero still reads as an increase.
export const percentChange = (current: number, previous: number): number | null => {
    if (!previous || !Number.isFinite(previous) || !Number.isFinite(current)) return null;
    return Math.round(((current - previous) / Math.abs(previous)) * 100);
};
